import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

export function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { href: '/#features', label: t('nav.features') },
    { href: '/#pricing', label: t('nav.pricing') },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    const [path, hash] = href.split('#');

    if (location.pathname === '/' || location.pathname === path) {
      // Ya estamos en home, solo hacer scroll
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navegar al home con el hash
      navigate(href);
    }
  };

  return (
    <header
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg shadow-sm'
          : 'bg-transparent'
      )}
    >
      <Container>
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Affogato" className="w-8 h-8" />
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              Affogato
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://affogato-dze.pages.dev"
              className="text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
            >
              {t('common.login')}
            </a>
            <Button size="sm" disabled>
              {t('common.comingSoon')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6 text-gray-900 dark:text-white" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-gray-900 dark:text-white" />
            )}
          </button>
        </nav>
      </Container>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-800"
          >
            <Container>
              <div className="py-4 space-y-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="block text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="https://affogato-dze.pages.dev"
                  className="block text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
                >
                  {t('common.login')}
                </a>
                <Button className="w-full" disabled>
                  {t('common.comingSoon')}
                </Button>
              </div>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
